import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import {
  Field,
  FieldLabel,
} from "@/frontend/components/ui/field";
import { authClient, signIn } from "../../auth-client";
import { orpcClient } from "@/frontend/orpc-client";
import { cn } from "@/frontend/lib/utils";
import { siteUrl } from "@/lib/docs";
import { SplitGridLayout } from "../shared/SplitGridLayout";
import { NameStep } from "../shared/NameStep";
import { GoogleLogo, MicrosoftLogo } from "@/frontend/components/icons/BrandLogos";
import type { OtpStep, SsoProvider } from "@/frontend/types/auth";
import {
  getOtpSessionFromLocalStorage,
  setOtpSessionInLocalStorage,
  clearOtpSessionFromLocalStorage,
  consumeRedirectFromUrl,
  consumeErrorFromUrl,
  hasOAuthRequest,
  resumeOAuthAuthorize,
} from "@/frontend/utils/auth";
import { OTP_RESEND_COOLDOWN_SECONDS } from "@/frontend/types/auth";

/**
 * Focuses the code field as it mounts, which only happens on the OTP step. It
 * lives at module scope so its identity is stable, since React re-runs a ref
 * callback whenever the callback itself changes and the cooldown timer
 * re-renders this component once a second.
 */
const focusOnMount = (node: HTMLInputElement | null) => {
  node?.focus();
};

export function SplitGridLoginView() {
  // Lazy initializer ensures getOtpSessionFromLocalStorage runs exactly once on mount, so a
  // stray re-render can't accidentally re-evaluate against an empty session
  // and reset the input.
  const [email, setEmail] = useState<string>(() => getOtpSessionFromLocalStorage().email);
  const [name, setName] = useState<string>(() => getOtpSessionFromLocalStorage().name);
  const [otp, setOtp] = useState("");
  // "name" is transient — never restore it. Existing users would skip past it
  // via the email-step existence check, so showing it on page load would be
  // wrong. Only "email" and "otp" round-trip through localStorage.
  const [step, setStep] = useState<OtpStep>(() => {
    const persisted = getOtpSessionFromLocalStorage().step;
    return persisted === "name" ? "email" : persisted;
  });
  // Sanitize the post-login redirect: same-origin path only. Anything else
  // (absolute URLs, protocol-relative `//evil.com`, etc.) falls back to "/"
  // so the OAuth round-trip can't be hijacked to redirect users off-app.
  const [safeRedirect] = useState<string>(() => consumeRedirectFromUrl());
  const [error, setError] = useState<string | null>(() => consumeErrorFromUrl());
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<SsoProvider | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  // Synchronous latch — the `disabled={!!ssoLoading}` prop doesn't paint until
  // after React commits, so a fast Google→Microsoft double-tap fires two
  // overlapping OAuth flows and clobbers the state cookie.
  const ssoInFlightRef = useRef(false);

  // Bfcache restore preserves state — reset SSO loading on back from OAuth.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      ssoInFlightRef.current = false;
      setSsoLoading(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Resend cooldown timer. setInterval is an inherent side effect, so this
  // is one of the legitimate uses of useEffect (per the React docs: "If your
  // Effect doesn't synchronize with an external system, you don't need an
  // Effect" — a timer IS an external system here).
  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const id = setInterval(() => setResendSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendSecondsLeft]);

  const handleSso = async (provider: SsoProvider) => {
    if (ssoInFlightRef.current) return;
    ssoInFlightRef.current = true;
    setError(null);
    setSsoLoading(provider);
    try {
      // When resuming an OAuth authorize, return to this URL (signed query intact) so the render-time check can re-enter authorize.
      const callbackURL = hasOAuthRequest() ? window.location.href : safeRedirect;
      const result = await signIn.social({ provider, callbackURL, errorCallbackURL: callbackURL });
      if (result?.error) {
        setError(result.error.message || `Failed to sign in with ${provider.toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')  }`);
        ssoInFlightRef.current = false;
        setSsoLoading(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to sign in with ${provider}`);
      ssoInFlightRef.current = false;
      setSsoLoading(null);
    }
  };

  /**
   * Send the OTP and advance to the verification step. Used by both the
   * email-step (when the email belongs to an existing user) and the name-step
   * (after a new signup has typed their name).
   */
  const sendOtpAndAdvance = async () => {
    const result = await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in" });
    if (result.error) {
      setError(result.error.message || "Failed to send code");
      return false;
    }
    setStep("otp");
    setOtpSessionInLocalStorage("otp", email, name);
    setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
    return true;
  };

  /**
   * Step 1 → check whether the email belongs to an existing user. Returning
   * users skip the name step and go straight to OTP; brand-new emails route
   * through the name screen first so we can seed `user.name` before signup.
   */
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const { exists } = await orpcClient.org.checkUserExists({ email });
      if (exists) {
        await sendOtpAndAdvance();
      } else {
        setStep("name");
        setOtpSessionInLocalStorage("name", email, name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  /** Step 2 (new users only) → send OTP, then move to the verification screen. */
  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    setLoading(true);
    try {
      await sendOtpAndAdvance();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  /** Re-send the OTP without leaving the verification step. */
  const handleResendOtp = async () => {
    if (resendSecondsLeft > 0 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in" });
      if (result.error) {
        setError(result.error.message || "Failed to resend code");
      } else {
        setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp({ email, otp, name });
      if (result.error) {
        setError(result.error.message || "Invalid code");
      } else {
        clearOtpSessionFromLocalStorage();
        if (hasOAuthRequest()) {
          resumeOAuthAuthorize();
          return;
        }
        await authClient.getSession({ query: { disableCookieCache: true } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitGridLayout
      contentClassName="max-w-sm"
      leftContent={
        <div className="max-w-lg text-center">
          <div className="mb-4">
            <img src="/assets/medblocks.webp" alt="Medblocks" className="mx-auto size-24 object-contain" />
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight">
            One integration for all healthcare data.
          </h2>
          <p className="text-lg text-muted-foreground">
            So you can focus on building, not integrating.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <span className="rounded-full border border-border/60 bg-background/80 px-4 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur">
              1,000 patients free
            </span>
            <span className="rounded-full border border-border/60 bg-background/80 px-4 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur">
              No credit card required
            </span>
          </div>
        </div>
      }
    >
      <div className={cn("flex flex-col gap-6")}>
        {step === "email" && (
          <div className="flex flex-col items-center gap-1 text-center">
            <h1 className="text-2xl font-bold">Sign in to your account</h1>
            <p className="text-sm text-balance text-muted-foreground">
              Use Google, Microsoft, or sign in with your email
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {step === "email" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={!!ssoLoading}
                onClick={() => handleSso("google")}
                className="h-11"
              >
                {ssoLoading === "google" ? (
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                ) : (
                  <GoogleLogo />
                )}
                Google
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!!ssoLoading}
                onClick={() => handleSso("microsoft")}
                className="h-11"
              >
                {ssoLoading === "microsoft" ? (
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                ) : (
                  <MicrosoftLogo />
                )}
                Microsoft
              </Button>
            </div>

            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
              <span className="relative z-10 bg-background px-2 text-muted-foreground">or</span>
            </div>

            <form onSubmit={handleEmailContinue} className="flex flex-col gap-4">
              <Field>
                <FieldLabel htmlFor="email">Email Address</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setOtpSessionInLocalStorage("email", e.target.value, name);
                  }}
                  required
                  autoComplete="email"
                  className="h-11"
                />
              </Field>
              <Button type="submit" disabled={loading} className="h-11 w-full bg-black text-white hover:bg-gray-900">
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" data-icon="inline-start" />
                    Sending...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                By continuing, you agree to our{" "}
                <a href={siteUrl("/terms")} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  terms
                </a>{" "}
                and{" "}
                <a href={siteUrl("/privacy")} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  privacy policy
                </a>
                .
              </p>
            </form>
          </>
        )}

        {step === "name" && (
          <NameStep
            value={name}
            onChange={(v) => {
              setName(v);
              setOtpSessionInLocalStorage("name", email, v);
            }}
            onSubmit={handleRequestOtp}
            submitting={loading}
            submittingLabel="Sending..."
            footer={
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setError(null);
                  setOtpSessionInLocalStorage("email", email, name);
                }}
                className="text-center text-sm text-[#1A73E8] hover:underline"
              >
                Back
              </button>
            }
          />
        )}

        {step === "otp" && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-1 text-center">
              <h1 className="text-2xl font-bold">Enter verification code</h1>
            </div>

            <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
              <Field>
                <FieldLabel htmlFor="otp">Verification Code</FieldLabel>
                <Input
                  ref={focusOnMount}
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  required
                  autoComplete="one-time-code"
                  className="h-11 text-center tracking-widest"
                />
              </Field>

              <p className="text-center text-xs text-muted-foreground">
                We&apos;ve sent a verification code to <span data-ph-mask>{email}</span>
              </p>

              {/* Resend control — shows countdown until eligible. */}
              <p className="text-center text-xs text-muted-foreground">
                {resendSecondsLeft > 0 ? (
                  <>Resend code in {resendSecondsLeft}s</>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                  >
                    Resend code
                  </button>
                )}
              </p>

              <Button type="submit" disabled={loading || otp.length !== 6} className="h-11 w-full bg-black text-white hover:bg-gray-900">
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" data-icon="inline-start" />
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                By continuing, you agree to our{" "}
                <a href={siteUrl("/terms")} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  terms
                </a>{" "}
                and{" "}
                <a href={siteUrl("/privacy")} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  privacy policy
                </a>
                .
              </p>

              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setOtp("");
                  setError(null);
                  setOtpSessionInLocalStorage("email", email, name);
                  setResendSecondsLeft(0);
                }}
                className="text-center text-sm text-[#1A73E8] hover:underline"
              >
                Back to email
              </button>
            </form>
          </div>
        )}
      </div>
    </SplitGridLayout>
  );
}
